// ExportPopup.jsx
import React, { useState } from 'react';
import { X, FileDown } from 'lucide-react';
import { pdf } from '@react-pdf/renderer';
import { IndentPDF } from './IndentPDF';
import * as XLSX from 'xlsx';
import { differenceInDays, parseISO } from 'date-fns';

export const ExportPopup = ({ onClose, exportEndpoint, alertData, customFilename = 'export' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const processAlertData = (rawData) => {
    if (!rawData) return [];
    
    const dataArray = Array.isArray(rawData) ? rawData : [rawData];
    
    return dataArray.filter(Boolean).map(item => {
      // Calculate days left using local time zone
      let daysLeft = 0;
      if (item.CommitedDate) {
        try {
          const dueDate = parseISO(item.CommitedDate);
          const today = new Date();
          daysLeft = differenceInDays(dueDate, today);
        } catch (err) {
          console.error('Error parsing date:', err);
          daysLeft = item.daysLeft || 0;
        }
      } else {
        daysLeft = item.daysLeft || 0;
      }
      
      return {
        indentId: item.IndentID || 'N/A',
        itemName: item.ItemName || 'N/A',
        itemId: item.ItemID || 'N/A',
        quantity: item.RequestedQuantity || 0,
        qtyreceived: item.QuantityReceived || 0,
        unit: item.UnitName || 'N/A',
        projectName: item.ProjectName || 'N/A',
        createdBy: item.CreatedBy || 'N/A',
        dueDate: item.CommitedDate || 'N/A',
        daysLeft: daysLeft,
        status: daysLeft < 0 ? `${Math.abs(daysLeft)} days overdue` : `${daysLeft} days left`,
        description: item.Description || 'N/A',
        remarks: item.Remarks || 'N/A'
      };
    });
  };

  // Function to escape CSV values properly
  const escapeCSVValue = (value) => {
    if (value === null || value === undefined) return '';
    
    // Convert to string
    const stringValue = String(value);
    
    // Check if the value contains commas, quotes, or newlines
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') || stringValue.includes('\r')) {
      // Escape quotes by doubling them and wrap the entire value in quotes
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    
    // For simple values, just return as is
    return stringValue;
  };

  // Function to manually create CSV content with proper formatting
  const createCSVContent = (data) => {
    if (!data || data.length === 0) return '';
    
    // Define headers in the desired order
    const headers = [
      'S.No',
      'Indent ID',
      'Item Name',
      'Item ID',
      'Quantity',
      'Quantity Received',
      'Unit',
      'Project Name',
      'Created By',
      'Due Date',
      'Days Left',
      'Status',
      'Indent Remarks',
      'Received Remarks'
    ];
    
    // Create CSV header row with proper escaping
    let csvContent = headers.map(header => escapeCSVValue(header)).join(',') + '\n';
    
    // Add data rows
    data.forEach((item, index) => {
      const row = [
        escapeCSVValue(index + 1),
        escapeCSVValue(item.indentId),
        escapeCSVValue(item.itemName),
        escapeCSVValue(item.itemId),
        escapeCSVValue(item.quantity),
        escapeCSVValue(item.qtyreceived),
        escapeCSVValue(item.unit),
        escapeCSVValue(item.projectName),
        escapeCSVValue(item.createdBy),
        escapeCSVValue(item.dueDate),
        escapeCSVValue(item.daysLeft),
        escapeCSVValue(item.status),
        escapeCSVValue(item.description),
        escapeCSVValue(item.remarks)
      ];
      
      csvContent += row.join(',') + '\n';
    });
    
    return csvContent;
  };

  const handleExport = async (format) => {
    setIsLoading(true);
    setError('');
    
    try {
      // Process the data for all export formats
      const processedData = processAlertData(alertData);
      console.log("Processed data for export:", processedData);
      
      // Add more detailed logging for PDF export
      if (format === 'pdf') {
        console.log("PDF export - First item status:", processedData[0]?.status);
        console.log("PDF export - First item daysLeft:", processedData[0]?.daysLeft);
      }
      
      if (!processedData || processedData.length === 0) {
        throw new Error('No data available for export');
      }
      
      if (format === 'pdf') {
        const blob = await pdf(
          <IndentPDF data={processedData} orientation="portrait" />
        ).toBlob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${customFilename}.pdf`;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
        onClose();
        return;
      }
      
      // For CSV and Excel, use XLSX library to generate files locally
      if (format === 'csv' || format === 'xlsx') {
        if (format === 'csv') {
          // Use our custom CSV generation function for better control
          const csvContent = createCSVContent(processedData);
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${customFilename}.csv`;
          document.body.appendChild(a);
          a.click();
          URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          // For Excel, create a worksheet from the processed data
          const ws = XLSX.utils.json_to_sheet(processedData);
          
          // Create a workbook and add the worksheet
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, 'Alerts');
          
          // Write the Excel file
          XLSX.writeFile(wb, `${customFilename}.xlsx`);
        }
        
        onClose();
        return;
      }
      
      throw new Error(`Unsupported export format: ${format}`);
    } catch (err) {
      console.error('Export error:', err);
      setError(`Failed to export data: ${err.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">Export Data</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          {error && <div className="text-red-500 text-sm mb-4">{error}</div>}
          <button
            onClick={() => handleExport('csv')}
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition-colors duration-200"
          >
            <FileDown className="mr-2 w-4 h-4" />
            Export as CSV
          </button>
          <button
            onClick={() => handleExport('xlsx')}
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition-colors duration-200"
          >
            <FileDown className="mr-2 w-4 h-4" />
            Export as Excel
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition-colors duration-200"
          >
            <FileDown className="mr-2 w-4 h-4" />
            Export as PDF
          </button>
        </div>
      </div>
    </div>
  );
};